import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'

import { alpha } from '@mui/material/styles'
import { Button, Menu, MenuItem } from '@mui/material'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import OutputRoundedIcon from '@mui/icons-material/OutputRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser, updateMyProfile } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

import { useDeleteWaitingRoom } from '../model/useDeleteWaitingRoom'
import { useWaitingRoomJoined } from '../model/useWaitingRoomJoined'
import { useLeaveWaitingRoom } from '../model/useLeaveWaitingRoom'

export function ActionsButton({ waitingRoomId }: { waitingRoomId: string }) {
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId)

  const isCreator = authUser?._id === waitingRoom?.createdBy
  const waitingRoomJoined = useWaitingRoomJoined(waitingRoom)

  return (
    <PopupState variant="popover" popupId="waiting-room-actions">
      {(popupState) => (
        <>
          <Button endIcon={<KeyboardArrowDownIcon />} variant="contained" {...bindTrigger(popupState)}>
            {t('common:button.action.label')}
          </Button>
          <Menu
            elevation={0}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: '6px',
                marginTop: (theme) => theme.spacing(1),
                minWidth: 180,
                color: 'rgb(55, 65, 81)',
                boxShadow:
                  'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
                '& .MuiMenu-list': {
                  padding: '4px 0',
                },
                '& .MuiMenuItem-root': {
                  '& .MuiSvgIcon-root': {
                    fontSize: 18,
                    color: (theme) => theme.palette.text.secondary,
                    marginRight: (theme) => theme.spacing(1.5),
                  },
                  '&:active': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                  },
                },
              },
            }}
            {...bindMenu(popupState)}
          >
            <FavoriteWaitingRoomButton closePopup={popupState.close} waitingRoomId={waitingRoomId} />
            {waitingRoomJoined && <LeaveWaitingRoomButton closePopup={popupState.close} waitingRoomId={waitingRoomId} />}
            {isCreator && <DeleteWaitingRoomButton waitingRoomId={waitingRoomId} closePopup={popupState.close} />}
          </Menu>
        </>
      )}
    </PopupState>
  )
}

function DeleteWaitingRoomButton({ closePopup, waitingRoomId }: { closePopup: () => void; waitingRoomId: string }) {
  const { t } = useTranslation()
  const { deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen, isDeleting, handleDeleteWaitingRoom } = useDeleteWaitingRoom(waitingRoomId)

  return (
    <>
      <MenuItem
        onClick={() => {
          setDeleteConfirmationDialogOpen(true)
          closePopup()
        }}
      >
        <DeleteRoundedIcon />
        {t('page.waitingRoom.action.deleteWaitingRoomButton.label')}
      </MenuItem>
      <DeleteConfirmationDialog
        isDeleting={isDeleting}
        onClose={() => setDeleteConfirmationDialogOpen(false)}
        onDelete={handleDeleteWaitingRoom}
        open={deleteConfirmationDialogOpen}
        title={t('dialog.deleteConfirmation.waitingRoom.title')}
        message={t('dialog.deleteConfirmation.waitingRoom.message')}
      />
    </>
  )
}

function FavoriteWaitingRoomButton({ closePopup, waitingRoomId }: { closePopup: () => void; waitingRoomId: string }) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const authUser = useAppSelector(selectAuthUser)

  if (!isStaff(authUser)) return null

  const isFavorite = authUser.favoriteWaitingRooms?.includes(waitingRoomId)

  const handleToggleFavorite = () => {
    const currentFavorites = authUser.favoriteWaitingRooms ?? []
    const favoriteWaitingRooms = isFavorite ? currentFavorites.filter((id) => id !== waitingRoomId) : [...currentFavorites, waitingRoomId]
    dispatch(updateMyProfile({ favoriteWaitingRooms }))
    closePopup()
  }

  return (
    <MenuItem onClick={handleToggleFavorite}>
      {isFavorite ? <StarRoundedIcon color="primary" /> : <StarBorderRoundedIcon />}
      {isFavorite ? t('table.waitingRooms.actions.removeFromFavorites.label') : t('table.waitingRooms.actions.addToFavorites.label')}
    </MenuItem>
  )
}

function LeaveWaitingRoomButton({ closePopup, waitingRoomId }: { closePopup: () => void; waitingRoomId: string }) {
  const { t } = useTranslation()
  const { leaveWaitingRoom } = useLeaveWaitingRoom(waitingRoomId)
  return (
    <MenuItem
      onClick={async () => {
        await leaveWaitingRoom()
        closePopup()
      }}
    >
      <OutputRoundedIcon />
      {t('page.waitingRoom.action.leaveWaitingRoomButton.label')}
    </MenuItem>
  )
}
